import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { formatDecimalTr } from "../../Helper/FormatDecimalTr";

interface NullFlightEntry {
  flightDate: string;
  flightNumber: string;
  estimatedHours?: number | null;
  estimationSource?: 0 | 1;
  matchedFr24Id?: string | null;
  matchedFlightDate?: string | null;
}

const estimationSourceLabel = (source?: 0 | 1): string => {
  if (source === 1) return "Eşleşen uçuştan alındı";
  if (source === 0) return "Ay ortalamasından tahmin edildi";
  return "—";
};

interface AircraftUtilizationNullFlightsPopupProps {
  open: boolean;
  record: any;
  handleClose: () => void;
}

const AircraftUtilizationNullFlightsPopup: React.FC<AircraftUtilizationNullFlightsPopupProps> = ({
  open,
  record,
  handleClose,
}) => {
  const nullFlights: NullFlightEntry[] = record?.nullFlights ?? [];
  const flightCycle: number = record?.frCycle ?? 0;
  const avgMissingHours: number | null = record?.avgMissingHours ?? null;
  const estAddHours: number | null = record?.estAddHours ?? null;
  const estimatedFlightHours: number | null = record?.estimatedFlightHours ?? null;

  const hasFrData = record?.frCycle != null && record?.frHour != null && record?.frMinute != null;
  const foundFlightCount = flightCycle - nullFlights.length;

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>Flights with null datetime_landed</DialogTitle>
      <Divider />
      <DialogContent>
        {nullFlights.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Flight Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Flight Number</strong>
                </TableCell>
                <TableCell>
                  <strong>Tahmini Saat</strong>
                </TableCell>
                <TableCell>
                  <strong>Kaynak</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nullFlights.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.flightDate}</TableCell>
                  <TableCell>
                    <a
                      style={{
                        color: "#3f51b5",
                      }}
                      rel="noopener noreferrer"
                      target="_blank"
                      href={"https://www.flightradar24.com/data/flights/" + entry.flightNumber}
                    >
                      {entry.flightNumber}
                    </a>
                  </TableCell>
                  <TableCell>{formatDecimalTr(entry.estimatedHours)}</TableCell>
                  <TableCell>
                    {estimationSourceLabel(entry.estimationSource)}
                    {entry.estimationSource === 1 && (entry.matchedFlightDate || entry.matchedFr24Id) && (
                      <div style={{ fontSize: 11, color: "#757575" }}>
                        Eşleşen: {entry.matchedFlightDate ?? "—"} / {entry.matchedFr24Id ?? "—"}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {hasFrData ? "No null flights found." : "No null flight records. FR data is incomplete."}
          </Typography>
        )}

        <Divider style={{ margin: "16px 0" }} />

        <Typography variant="body2" style={{ marginBottom: 4 }}>
          <strong>Flight Cycle:</strong> {flightCycle} &nbsp;&nbsp;
          <strong>Null Flight Count:</strong> {nullFlights.length} &nbsp;&nbsp;
          <strong>Found Flight Count:</strong> {foundFlightCount}
        </Typography>
        <Typography variant="body2">
          <strong>Average Missing Duration (hour):</strong> {formatDecimalTr(avgMissingHours)} &nbsp;&nbsp;
          <strong>Added Duration (hour):</strong> {formatDecimalTr(estAddHours)} &nbsp;&nbsp;
          <strong>Total Estimated Duration (FR + Added):</strong> {formatDecimalTr(estimatedFlightHours)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AircraftUtilizationNullFlightsPopup;
